// Copyright (c) 2026 Theiagen Genomics. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use is prohibited.
// Pure, immutable CRUD over an annotation list. Shared by both clade
// annotations and leaf-label annotations — they have the identical array shape
// (plain objects keyed by a unique `id`), differing only in their style
// defaults and id prefix. No DOM, no callbacks: the class shell fires the
// callbacks and calls setProps with the returned list.

// Generate an id of the form `<prefix>-<n>` that does not collide with any id
// already in the list.
export function nextAnnotationId(list, prefix = "annotation") {
  const used = new Set(list.map((a) => a.id));
  let n = list.length + 1;
  let id = `${prefix}-${n}`;
  while (used.has(id)) {
    n += 1;
    id = `${prefix}-${n}`;
  }
  return id;
}

// Append a new entry, merging style defaults under the caller's input. An
// explicit `input.id` wins; otherwise a collision-free id is generated.
// Returns the new list and the created entry (for the callback payload).
export function createAnnotationEntry(
  list,
  input = {},
  { defaults = {}, prefix = "annotation" } = {},
) {
  const id = input.id != null ? input.id : nextAnnotationId(list, prefix);
  const entry = { ...defaults, ...input, id };
  return { list: [...list, entry], entry };
}

// Shallow-merge `patch` into the entry with matching id. The id is preserved.
// Unknown id is a no-op: returns the same list reference and a null entry.
export function updateAnnotationEntry(list, id, patch = {}) {
  let entry = null;
  const next = list.map((a) => {
    if (a.id !== id) return a;
    entry = { ...a, ...patch, id };
    return entry;
  });
  return { list: entry ? next : list, entry };
}

// Remove the entry with matching id. Unknown id is a no-op: returns the same
// list reference and a null entry; otherwise returns the new list and the
// removed entry.
export function deleteAnnotationEntry(list, id) {
  const entry = list.find((a) => a.id === id) ?? null;
  return { list: entry ? list.filter((a) => a.id !== id) : list, entry };
}
